import {
  Account_Read_Nested as AccountNestedProto,
  Contact_Create,
  Contact_Update_Destroyable,
  DeliveredQuantity_Read,
  ImportedOrder_Read,
  Order_Create,
  Order_Intake_Create,
  Order_Read,
  Order_Read_Minimal,
  Order_Read_Nested,
  Order_Update,
  OrderIntakeMetadata,
  OrderIntakeReviewField,
  OrderIntakeReviewItem,
  OrderIntakeReviewReason,
  OrderMaterialTotal_Read,
  OrderSource,
  OrderState,
  OrderStateEvent,
  OrderSummary_Read_Nested,
  OrderTimeline_Read,
  OrderUnitOfMeasure,
  Principal_Read_Nested,
  RateType,
  UnitOfDistance,
  User_Read,
  Waypoint_Create,
  Waypoint_Update_Destroyable,
  WaypointType,
} from '@treadinc/horizon-api-spec';
import dayjs, { Dayjs } from 'dayjs'; // Import other necessary types
import { cloneDeep, isNil } from 'lodash-es';

import { ContactTypes } from '~constants/enums';
import { AccountBasic } from '~hooks/useAccount/models';
import { CompanyBasic } from '~hooks/useCompany/models';
import { BasicDepartment } from '~hooks/useDepartment/models';
import { DispatchNumberBasic } from '~hooks/useDispatchNumbers/models';
import { EquipmentTypeBasic } from '~hooks/useEquipment/models';
import { MaterialRateTypeahead } from '~hooks/useMaterialRates/models';
import { BasicMaterial } from '~hooks/useMaterials/models';
import {
  contactTypeMap,
  ExtendedContactItem,
  filterContact,
  Phase,
  ProjectBasic,
  transformContact,
} from '~hooks/useProjects/models';
import { RateBasicWithValue } from '~hooks/useRates/models';
import { BasicServiceClass } from '~hooks/useServiceClass/models';
import { Service } from '~hooks/useServices/models';
import { SiteRateSchedule } from '~hooks/useSiteRateSchedule/models';
import { SiteBasic, WayPoint, WayPointFormProps } from '~hooks/useSites/models';
import { TaxRateScheduleBasic } from '~hooks/useTaxRateSchedule/models';
import { User, UserBasic } from '~hooks/useUsers/models';
import {
  deparseRateDetailsDTO,
  deparseUpdateRateDetailsDTO,
  OrderFormRateFields,
} from '~pages/Sales/Orders/NewOrderForm/schema';
import {
  NewOrderFormSchemaInterface,
  serviceIsStandingHaul,
} from '~pages/Sales/Orders/newOrderFormSchema';
import { ItemNameAndId } from '~types/ItemNameAndId';
import { Nullable } from '~types/Nullable';
import { convertToMeters, parseFiniteNumber } from '~utils/utilFunctions';

import { toOrderUnitOfMeasure, toRateType } from './deparseV3';

export class OrderMinimal {
  constructor(
    private _id: string,
    private _orderId: string,
  ) {}

  public get id() {
    return this._id;
  }

  public get orderId() {
    return this._orderId;
  }

  public static parse(proto: Order_Read_Minimal) {
    return new OrderMinimal(proto.id, proto.order_id);
  }
}

type OrderDeparseInput = NewOrderFormSchemaInterface &
  OrderFormRateFields & { phases?: Phase[] };

// An order's own material line. Mirrors ProjectMaterialTotal but without phase/site:
// an order is a daily slice, not a whole-job plan, and it carries its own pickup waypoints.
export class OrderMaterialTotal {
  public static parse(proto: OrderMaterialTotal_Read) {
    return new OrderMaterialTotal(
      proto.id ?? '',
      proto.total_quantity ?? '',
      proto.unit_of_measure
        ? ItemNameAndId.parse({
            name: proto.unit_of_measure,
            id: proto.unit_of_measure,
          })
        : null,
      proto.material ? BasicMaterial.parse(proto.material) : null,
    );
  }
  public get id() {
    return this._id;
  }
  public get material() {
    return this._material;
  }
  public set material(value: Nullable<BasicMaterial>) {
    this._material = value;
  }
  public get unitOfMeasure() {
    return this._unit_of_measure;
  }
  public set unitOfMeasure(value: Nullable<ItemNameAndId>) {
    this._unit_of_measure = value;
  }
  public get totalQuantity() {
    return this._total_quantity;
  }
  public set totalQuantity(value: string) {
    this._total_quantity = value;
  }
  constructor(
    private _id: string,
    private _total_quantity: string,
    private _unit_of_measure: Nullable<ItemNameAndId>,
    private _material: Nullable<BasicMaterial>,
  ) {}
}

export class IntakeReviewItem {
  constructor(
    private _field: OrderIntakeReviewField,
    private _reason: OrderIntakeReviewReason,
    private _text: string,
  ) {}

  public static parse(proto: OrderIntakeReviewItem) {
    return new IntakeReviewItem(proto.field, proto.reason, proto.text);
  }

  public get field() {
    return this._field;
  }

  public get reason() {
    return this._reason;
  }

  public get text() {
    return this._text;
  }
}

class IntakeMetadata {
  constructor(private _review_items: IntakeReviewItem[]) {}

  public static parse(proto: OrderIntakeMetadata) {
    return new IntakeMetadata(proto.review_items.map(IntakeReviewItem.parse));
  }

  public get reviewItems() {
    return this._review_items;
  }
}

type OrderContactPayload = Contact_Create | Contact_Update_Destroyable | null;

interface OrderContacts {
  foremanContact: OrderContactPayload;
  salesContact: OrderContactPayload;
  collaboratorContact: OrderContactPayload;
  supervisorContact: OrderContactPayload;
}

export class Order {
  public static parse(proto: Order_Read): Order {
    return new Order(
      proto.id ?? '',
      proto.editable ?? false,
      proto.created_at?.length ? dayjs(proto.created_at) : null,
      proto.company ? CompanyBasic.parse(proto.company) : null,
      proto.job_start_at?.length ? dayjs(proto.job_start_at) : null,
      proto.load_at?.length ? dayjs(proto.load_at) : null,
      proto.notes ?? null,
      proto.job_notes ?? null,
      proto.job_time ?? null,
      proto.internal_notes ?? null,
      proto.state ?? null,
      proto.truck_count ?? null,
      proto.truck_delay ?? null,
      proto.trucks_per_delay ?? null,
      proto.loads_per_truck ?? null,
      proto.load_cycle_avg ?? 0,
      proto.unit_of_measure
        ? ItemNameAndId.parse({
            name: proto.unit_of_measure,
            id: proto.unit_of_measure,
          })
        : null,
      proto.account ? AccountBasic.parse(proto.account) : null, // Account is another type that needs to be defined
      proto.order_id,
      proto.project ? ProjectBasic.parse(proto.project) : null,
      proto.material ? BasicMaterial.parse(proto.material) : null,
      proto?.equipment_type ? EquipmentTypeBasic.parse(proto.equipment_type) : null,
      parseFiniteNumber(proto.quantity),
      proto.service ? Service.parse(proto.service) : null,
      proto?.waypoints?.length ? proto.waypoints.map(WayPoint.parse) : null,
      proto?.waypoints?.length
        ? proto.waypoints
            .filter((point) => {
              return point.type === WaypointType.PICKUP;
            })
            .map(WayPoint.parse)
        : null,
      proto?.waypoints?.length
        ? proto.waypoints
            .filter((point) => {
              return point.type === WaypointType.DROP_OFF;
            })
            .map(WayPoint.parse)
        : null,
      proto?.waypoints?.length
        ? proto.waypoints
            .filter((point) => {
              return point.type === WaypointType.STAGING;
            })
            .map(WayPoint.parse)
        : null,

      proto?.vendor_rate ? RateBasicWithValue.parse(proto.vendor_rate) : null,
      proto?.customer_rate ? RateBasicWithValue.parse(proto.customer_rate) : null,
      proto.phase ? Phase.parse(proto.phase) : null,
      proto.phases ? proto.phases.map(Phase.parse) : [],
      proto.sales_contact
        ? ExtendedContactItem.parse({ ...proto.sales_contact, type: ContactTypes.SALES })
        : null,
      proto.foreman_contact
        ? ExtendedContactItem.parse({
            ...proto.foreman_contact,
            type: ContactTypes.FOREMAN,
          })
        : null,
      proto.collaborator_contact
        ? ExtendedContactItem.parse({
            ...proto.collaborator_contact,
            type: ContactTypes.COLLABORATOR,
          })
        : null,
      proto.supervisor_contact
        ? ExtendedContactItem.parse({
            ...proto.supervisor_contact,
            type: ContactTypes.SUPERVISOR,
          })
        : null,
      proto.department ? BasicDepartment.parse(proto.department) : null,
      proto.service_class ? BasicServiceClass.parse(proto.service_class) : null,
      proto.action_required ?? false,
      proto.foremen?.length
        ? proto?.foremen.map((item) => User.parse(item as User_Read))
        : [],
      proto.name,
      proto.po_job_number,
      proto.zone,
      proto.external_id,
      proto.hauler_rate_type ? (proto.hauler_rate_type as RateType) : null,
      proto.units_per_hour ?? null,
      parseFiniteNumber(proto.job_quantity),
      proto.job_unit_of_measure
        ? ItemNameAndId.parse({
            name: proto.job_unit_of_measure,
            id: proto.job_unit_of_measure,
          })
        : null,
      proto.sending_accounts ?? [],
      proto.dispatch_number ? DispatchNumberBasic.parse(proto.dispatch_number) : null,
      proto.contract_number ?? null,
      parseFiniteNumber(proto.customer_rate_value),
      proto.customer_rate_type ?? null,
      parseFiniteNumber(proto.vendor_rate_value),
      proto.vendor_rate_type ?? null,
      proto.driver_rate ? RateBasicWithValue.parse(proto.driver_rate) : null,
      parseFiniteNumber(proto.driver_rate_value),
      proto.driver_rate_type ?? null,
      proto.loads_pickup_time_minutes_avg,
      proto.loads_dropoff_time_minutes_avg,
      proto.requester ? UserBasic.parse(proto.requester) : null,
      proto.order_summary ? OrderSummaryBasic.parse(proto.order_summary) : null,
      parseFiniteNumber(proto.equipment_type_gross_capacity),
      proto.equipment_type_gross_capacity_unit_of_measure
        ? ItemNameAndId.parse({
            name: proto.equipment_type_gross_capacity_unit_of_measure,
            id: proto.equipment_type_gross_capacity_unit_of_measure,
          })
        : null,
      proto.pickup_onsite_minutes,
      proto.dropoff_onsite_minutes,
      proto.pickup_to_dropoff_time_minutes,
      isNil(proto.pickup_to_dropoff_distance_meters)
        ? null
        : Number(proto.pickup_to_dropoff_distance_meters),
      proto.expected_total_loads,
      proto.cycle_time_minutes,
      proto.recurring_loads,
      (proto.collaborators ?? []).map((user) => UserBasic.parse(user)),
      proto.material_rate ? MaterialRateTypeahead.parse(proto.material_rate) : null,
      proto.material_rate_site ? SiteBasic.parse(proto.material_rate_site) : null,
      proto.material_rate_unit_of_measure,
      isNil(proto.material_rate_sell_price)
        ? null
        : Number(proto.material_rate_sell_price),
      isNil(proto.material_rate_buy_price) ? null : Number(proto.material_rate_buy_price),
      proto.reject_reason,
      proto.state_events,
      proto.material_rate_itemized ?? false,
      proto.geofence_delay_seconds ?? null,
      proto.load_unit_of_measure
        ? ItemNameAndId.parse({
            name: proto.load_unit_of_measure,
            id: proto.load_unit_of_measure,
          })
        : null,
      proto.charge_to_account ?? null,
      proto.chase_missing_tickets ?? false,
      proto.moving_site_sync_enabled ?? true,
      proto.default_digital_ticket_view,
      proto.jobs_splittable,
      proto.prevailing_wage,
      isNil(proto.tax_rate) ? null : Number(proto.tax_rate),
      isNil(proto.tax_freight) ? null : proto.tax_freight,
      isNil(proto.tax_material) ? null : proto.tax_material,
      isNil(proto.tax_add_ons) ? null : proto.tax_add_ons,
      proto.tax_rate_schedule
        ? TaxRateScheduleBasic.parse(proto.tax_rate_schedule)
        : null,
      proto.salespeople?.length
        ? proto?.salespeople.map((item) => User.parse(item as User_Read))
        : [],
      proto.default_site_rate_schedule
        ? SiteRateSchedule.parse(proto.default_site_rate_schedule)
        : null,
      proto.live_eta ?? false,
      proto.applicable_rate_card_count ?? null,
      proto.material_totals ? proto.material_totals.map(OrderMaterialTotal.parse) : [],
      proto.discarded_at ? dayjs(proto.discarded_at) : null,
      proto.source,
      proto.intake_metadata ? IntakeMetadata.parse(proto.intake_metadata) : null,
      proto.messaging_enabled,
      proto.vendor_pay_commission_percentage ?? null,
      proto.driver_pay_commission_percentage ?? null,
    );
  }
  public static getOrderContacts(
    order: NewOrderFormSchemaInterface,
    isUpdate: boolean = false,
  ): OrderContacts {
    const contacts: OrderContacts = {
      foremanContact: null,
      salesContact: null,
      collaboratorContact: null,
      supervisorContact: null,
    };

    (Object.keys(contactTypeMap) as Array<keyof OrderContacts>).forEach((key) => {
      const contactType = contactTypeMap[key];
      const contact = filterContact(order, contactType);
      contacts[key] = transformContact(contact, isUpdate) as OrderContactPayload;
    });
    return contacts;
  }

  public static deparseUpdate(
    order: OrderDeparseInput,
    distanceUnit?: UnitOfDistance,
  ): Order_Update {
    const { foremanContact, salesContact, collaboratorContact, supervisorContact } =
      this.getOrderContacts(order, true);
    const isStandingHaulService = serviceIsStandingHaul(order.service);

    // When all three rate-related form fields are absent (form
    // omitted them because the user did not explicitly modify the rate),
    // pass undefined so deparse skips the *_rate_* fields and avoids
    // overwriting Site Rate Schedule cascade-written values.
    const customerRate = deparseUpdateRateDetailsDTO(
      'customer',
      order.customerRate === undefined &&
        order.customerRateType === undefined &&
        order.customerRateValue === undefined
        ? undefined
        : {
            targetRate: order.customerRate,
            targetRateType: order.customerRateType,
            targetRateValue: order.customerRateValue,
          },
    );
    const vendorRate = deparseUpdateRateDetailsDTO(
      'vendor',
      order.vendorRate === undefined &&
        order.vendorRateType === undefined &&
        order.vendorRateValue === undefined
        ? undefined
        : {
            targetRate: order.vendorRate,
            targetRateType: order.vendorRateType,
            targetRateValue: order.vendorRateValue,
          },
    );
    const driverRate = deparseUpdateRateDetailsDTO(
      'driver',
      order.driverRate === undefined &&
        order.driverRateType === undefined &&
        order.driverRateValue === undefined
        ? undefined
        : {
            targetRate: order.driverRate,
            targetRateType: order.driverRateType,
            targetRateValue: order.driverRateValue,
          },
    );

    const pickUpWaypointId = order.pickUpWayPoint?.id;
    const dropOffWaypointId = order.dropOffWayPoint?.id;
    const stagingWaypointId = order.stagingWayPoint?.id;

    const pickUpWayPoint = (order.pickUpWayPoint as WayPoint)?.site?.id
      ? WayPoint.deparse(order.pickUpWayPoint as WayPointFormProps)
      : null;
    const dropOffWayPoint = (order.dropOffWayPoint as WayPoint)?.site?.id
      ? WayPoint.deparse(order.dropOffWayPoint as WayPointFormProps)
      : null;
    const stagingWayPoint = (order.stagingWayPoint as WayPoint)?.site?.id
      ? WayPoint.deparse(order.stagingWayPoint as WayPointFormProps)
      : null;

    const waypoints: Array<Waypoint_Update_Destroyable> | undefined = [
      ...(pickUpWayPoint
        ? [pickUpWayPoint]
        : pickUpWaypointId
          ? [{ id: pickUpWaypointId, _destroy: 1 }]
          : []),
      ...(dropOffWayPoint
        ? [dropOffWayPoint]
        : dropOffWaypointId
          ? [{ id: dropOffWaypointId, _destroy: 1 }]
          : []),
      ...(stagingWayPoint
        ? [stagingWayPoint]
        : stagingWaypointId
          ? [{ id: stagingWaypointId, _destroy: 1 }]
          : []),
    ];

    const body: Order_Update = {
      ...(order?.project?.id?.length ? { project_id: order.project.id } : {}),
      equipment_type_id: order?.equipmentType?.id as string,
      material_id: order?.material?.id ?? null,
      quantity: order?.quantity || null,
      unit_of_measure: toOrderUnitOfMeasure(order?.unitOfMeasure?.id),
      job_start_at: order.jobStartAt ? dayjs(order.jobStartAt).toISOString() : undefined,
      load_at: order.loadAt ? dayjs(order.loadAt).toISOString() : undefined,
      notes: order.orderNotes?.length ? order.orderNotes : '',
      job_notes: order.jobNotes?.length ? order.jobNotes : '',
      internal_notes: order.internalNotes?.length ? order.internalNotes : '',
      truck_count: order.truckCount,
      truck_delay: Number(order.truckDelay || 0),
      trucks_per_delay: Number(order.trucksPerDelay || 1),
      service_id: order?.service?.id || '',
      waypoints,
      phase: order?.phase?.name?.length ? Phase.deparse(order?.phase) : undefined,
      phases: order?.phases?.length
        ? order?.phases.map((phase) => Phase.deparseUpdate(phase))
        : [],
      ...customerRate,
      vendor_pay_commission_percentage: order.vendorPayCommissionPercentage,
      driver_pay_commission_percentage: order.driverPayCommissionPercentage,
      ...vendorRate,
      ...driverRate,
      sales_contact: salesContact
        ? ExtendedContactItem.deparseUpdate(salesContact)
        : undefined,
      foreman_contact: foremanContact
        ? ExtendedContactItem.deparseUpdate(foremanContact)
        : undefined,
      collaborator_contact: collaboratorContact
        ? ExtendedContactItem.deparseUpdate(collaboratorContact)
        : undefined,
      supervisor_contact: supervisorContact
        ? ExtendedContactItem.deparseUpdate(supervisorContact)
        : undefined,
      department_id: order?.department?.id || null,
      service_class_id: order?.serviceClass?.id || null,
      foreman_ids: order.foremen?.map((foreman) => foreman.id) || [],
      collaborator_ids: order.collaborators?.map((collaborator) => collaborator.id) || [],
      name: order.name || '',
      po_job_number: order.poJobNumber || '',
      zone: order.zone || '',
      external_id: order.externalId || '',
      units_per_hour: order.unitsPerHour ? Number(order.unitsPerHour) : null,
      job_quantity: order?.jobQuantity ?? (isStandingHaulService ? 0 : null),
      job_unit_of_measure: toOrderUnitOfMeasure(order?.jobUnitOfMeasure?.id),
      // see also identical logic in Project.deparseUpdate
      dispatch_number:
        order?.dispatchNumber && order.dispatchNumber.trim() !== ''
          ? { name: order.dispatchNumber }
          : order?.dispatchNumber === '' && order?.id
            ? { _destroy: 1 }
            : undefined,
      contract_number: order?.contractNumber || null,
      requester_id: order?.requester?.id || null,
      hauler_rate_type: toRateType(order.haulerRateType?.value),
      material_rate_id: order.materialRate?.id ?? null,
      material_rate_unit_of_measure: toOrderUnitOfMeasure(order.materialRateUoM?.id),
      material_rate_sell_price: isNil(order.materialRateSellPrice)
        ? null
        : Number(order.materialRateSellPrice) || null,
      material_rate_buy_price: isNil(order.materialRateBuyPrice)
        ? null
        : Number(order.materialRateBuyPrice) || null,
      geofence_delay_seconds: order.geofenceDelaySeconds ?? undefined,
      load_unit_of_measure: toOrderUnitOfMeasure(order?.loadUnitOfMeasure?.id),
      equipment_type_gross_capacity_unit_of_measure: toOrderUnitOfMeasure(
        order?.equipmentTypeGrossCapacityUnitOfMeasure?.id,
      ),
      charge_to_account: order.chargeToAccount ?? null,
      salesperson_ids: order.salespeople?.map((salesperson) => salesperson.id) || [],
    };

    body.job_time =
      isNil(order.jobTime) || `${order.jobTime}`.length === 0
        ? null
        : Number(order.jobTime) * 60;

    body.cycle_time_minutes = parseFiniteNumber(order.cycleTime);
    body.pickup_onsite_minutes = parseFiniteNumber(order.pickupOnsite);
    body.dropoff_onsite_minutes = parseFiniteNumber(order.dropoffOnsite);
    body.pickup_to_dropoff_time_minutes = parseFiniteNumber(order.pickupToDropoff);
    const parsedCycleDistance = parseFiniteNumber(order.cycleDistance);
    body.pickup_to_dropoff_distance_meters =
      parsedCycleDistance === null
        ? null
        : distanceUnit
          ? convertToMeters(parsedCycleDistance, distanceUnit)
          : parsedCycleDistance;
    body.expected_total_loads = parseFiniteNumber(order.totalLoads);
    body.equipment_type_gross_capacity = parseFiniteNumber(
      order.equipmentTypeGrossCapacity,
    );

    body.recurring_loads = Boolean(order.recurringLoads);
    body.loads_per_truck = undefined;

    body.material_rate_itemized = Boolean(order.materialRateItemized);
    body.chase_missing_tickets = Boolean(order.chaseMissingTickets);
    // Deliberately not coerced like its neighbours: partial callers (QuickOrderDialog)
    // leave liveEta undefined, and the BE only inherits the project's value when the
    // key is absent. Sending false here would clear an opted-in order.
    if (!isNil(order.liveEta)) {
      body.live_eta = order.liveEta;
    }
    body.moving_site_sync_enabled = Boolean(order.movingSiteSyncEnabled);
    body.default_digital_ticket_view = Boolean(order.defaultDigitalTicketView);
    body.jobs_splittable = Boolean(order.jobsSplittable);
    body.prevailing_wage = Boolean(order.prevailingWage);
    body.tax_rate = isNil(order.taxRate) ? null : Number(order.taxRate);
    body.tax_freight = order.taxFreight ?? false;
    body.tax_material = order.taxMaterial ?? false;
    body.tax_add_ons = order.taxAddOns ?? false;
    body.tax_rate_schedule_id = order.taxRateSchedule?.value ?? null;

    body.default_site_rate_schedule_id = order.defaultSiteRateScheduleId ?? null;

    return body;
  }

  public static deparse(
    order: OrderDeparseInput,
    distanceUnit?: UnitOfDistance,
  ): Order_Create {
    const { foremanContact, salesContact, collaboratorContact, supervisorContact } =
      this.getOrderContacts(order);

    // See deparseUpdate above — omit *_rate_* fields when the form
    // did not include them (user did not explicitly set a rate).
    const customerRate = deparseRateDetailsDTO(
      'customer',
      order.customerRate === undefined &&
        order.customerRateType === undefined &&
        order.customerRateValue === undefined
        ? undefined
        : {
            targetRate: order.customerRate,
            targetRateType: order.customerRateType,
            targetRateValue: order.customerRateValue,
          },
    );
    const vendorRate = deparseRateDetailsDTO(
      'vendor',
      order.vendorRate === undefined &&
        order.vendorRateType === undefined &&
        order.vendorRateValue === undefined
        ? undefined
        : {
            targetRate: order.vendorRate,
            targetRateType: order.vendorRateType,
            targetRateValue: order.vendorRateValue,
          },
    );
    const driverRate = deparseRateDetailsDTO(
      'driver',
      order.driverRate === undefined &&
        order.driverRateType === undefined &&
        order.driverRateValue === undefined
        ? undefined
        : {
            targetRate: order.driverRate,
            targetRateType: order.driverRateType,
            targetRateValue: order.driverRateValue,
          },
    );

    const pickUpWayPoint = order.pickUpWayPoint?.site?.id
      ? WayPoint.deparse(order.pickUpWayPoint as WayPointFormProps, true)
      : null;
    const dropOffWayPoint = order.dropOffWayPoint?.site?.id
      ? WayPoint.deparse(order.dropOffWayPoint as WayPointFormProps, true)
      : null;
    const stagingWayPoint = order.stagingWayPoint?.site?.id
      ? WayPoint.deparse(order.stagingWayPoint as WayPointFormProps, true)
      : null;
    const waypoints: Array<Waypoint_Create> | undefined = [
      pickUpWayPoint,
      dropOffWayPoint,
      stagingWayPoint,
    ].filter((waypoint): waypoint is Waypoint_Create => waypoint !== null);

    const body: Order_Create = {
      company_id: order?.company?.id as string,
      project_id: order?.project?.id?.length ? order?.project?.id : undefined,
      equipment_type_id: order?.equipmentType?.id as string,
      material_id: order?.material?.id as string,
      quantity: (order.quantity as number) || undefined,
      unit_of_measure: order.unitOfMeasure?.id as OrderUnitOfMeasure,
      job_start_at: order.jobStartAt ? dayjs(order.jobStartAt).toISOString() : undefined,
      load_at: order.loadAt ? dayjs(order.loadAt).toISOString() : '',
      notes: order.orderNotes?.length ? order.orderNotes : undefined,
      job_notes: order.jobNotes?.length ? order.jobNotes : undefined,
      internal_notes: order.internalNotes?.length ? order.internalNotes : undefined,
      truck_count: Number(order.truckCount || 0),
      truck_delay: Number(order.truckDelay || 0),
      trucks_per_delay: Number(order.trucksPerDelay || 1),
      loads_per_truck: order.loadsPerTruck ? Number(order.loadsPerTruck) : undefined,
      account_id: order?.account?.id || '',
      service_id: order?.service?.id || '',
      waypoints,
      phase: order?.phase?.name?.length ? Phase.deparse(order?.phase) : undefined,
      phases: order?.phases?.length
        ? order?.phases.map((phase) => Phase.deparse(phase))
        : [],
      ...customerRate,
      vendor_pay_commission_percentage: order.vendorPayCommissionPercentage,
      driver_pay_commission_percentage: order.driverPayCommissionPercentage,
      ...vendorRate,
      sales_contact: salesContact
        ? ExtendedContactItem.deparseUpdate(salesContact)
        : undefined,
      foreman_contact: foremanContact
        ? ExtendedContactItem.deparseUpdate(foremanContact)
        : undefined,
      collaborator_contact: collaboratorContact
        ? ExtendedContactItem.deparseUpdate(collaboratorContact)
        : undefined,
      supervisor_contact: supervisorContact
        ? ExtendedContactItem.deparseUpdate(supervisorContact)
        : undefined,
      department_id: order?.department?.id || null,
      service_class_id: order?.serviceClass?.id || null,
      foreman_ids: order.foremen?.map((foreman) => foreman.id) || [],
      collaborator_ids: order.collaborators?.map((collaborator) => collaborator.id) || [],
      name: order.name || '',
      po_job_number: order.poJobNumber || '',
      zone: order.zone || '',
      external_id: order.externalId || '',
      units_per_hour: order.unitsPerHour ? Number(order.unitsPerHour) : null,
      job_quantity: order.jobQuantity || null,
      job_unit_of_measure: toOrderUnitOfMeasure(order?.jobUnitOfMeasure?.id),
      dispatch_number: order?.dispatchNumber ? { name: order.dispatchNumber } : undefined,
      contract_number: order?.contractNumber || null,
      requester_id: order?.requester?.id || null,
      ...driverRate,
      hauler_rate_type: toRateType(order.haulerRateType?.value),
      material_rate_id: order.materialRate?.id ?? null,
      material_rate_site_id: order.materialRate?.site?.id ?? null,
      material_rate_unit_of_measure: toOrderUnitOfMeasure(order.materialRateUoM?.id),
      material_rate_sell_price: isNil(order.materialRateSellPrice)
        ? null
        : Number(order.materialRateSellPrice) || null,
      material_rate_buy_price: isNil(order.materialRateBuyPrice)
        ? null
        : Number(order.materialRateBuyPrice) || null,
      geofence_delay_seconds: order.geofenceDelaySeconds ?? null,
      load_unit_of_measure: toOrderUnitOfMeasure(order?.loadUnitOfMeasure?.id),
      charge_to_account: order.chargeToAccount ?? null,
      salesperson_ids: order.salespeople?.map((salesperson) => salesperson.id) || [],
    };

    body.job_time =
      isNil(order.jobTime) || `${order.jobTime}`.length === 0
        ? null
        : Number(order.jobTime) * 60;

    body.cycle_time_minutes = parseFiniteNumber(order.cycleTime);
    body.pickup_onsite_minutes = parseFiniteNumber(order.pickupOnsite);
    body.dropoff_onsite_minutes = parseFiniteNumber(order.dropoffOnsite);
    body.pickup_to_dropoff_time_minutes = parseFiniteNumber(order.pickupToDropoff);
    const parsedCycleDistance = parseFiniteNumber(order.cycleDistance);
    body.pickup_to_dropoff_distance_meters =
      parsedCycleDistance === null
        ? null
        : distanceUnit
          ? convertToMeters(parsedCycleDistance, distanceUnit)
          : parsedCycleDistance;
    body.expected_total_loads = parseFiniteNumber(order.totalLoads);
    body.equipment_type_gross_capacity = parseFiniteNumber(
      order.equipmentTypeGrossCapacity,
    );
    body.equipment_type_gross_capacity_unit_of_measure = toOrderUnitOfMeasure(
      order?.equipmentTypeGrossCapacityUnitOfMeasure?.id,
    );

    body.recurring_loads = Boolean(order.recurringLoads);
    body.loads_per_truck = undefined;

    body.material_rate_itemized = Boolean(order.materialRateItemized);
    body.chase_missing_tickets = Boolean(order.chaseMissingTickets);
    // Deliberately not coerced like its neighbours: partial callers (QuickOrderDialog)
    // leave liveEta undefined, and the BE only inherits the project's value when the
    // key is absent. Sending false here would clear an opted-in order.
    if (!isNil(order.liveEta)) {
      body.live_eta = order.liveEta;
    }
    body.moving_site_sync_enabled = Boolean(order.movingSiteSyncEnabled);
    body.default_digital_ticket_view = Boolean(order.defaultDigitalTicketView);
    body.jobs_splittable = Boolean(order.jobsSplittable);
    body.prevailing_wage = Boolean(order.prevailingWage);
    body.tax_rate = isNil(order.taxRate) ? null : Number(order.taxRate);
    body.tax_freight = order.taxFreight ?? false;
    body.tax_material = order.taxMaterial ?? false;
    body.tax_add_ons = order.taxAddOns ?? false;
    body.tax_rate_schedule_id = order.taxRateSchedule?.value ?? null;

    body.default_site_rate_schedule_id = order.defaultSiteRateScheduleId ?? null;

    return body;
  }
  public get id(): string {
    return this._id;
  }
  public get editable(): boolean {
    return this._editable;
  }
  public get createdAt(): Nullable<Dayjs> {
    return this._created_at;
  }
  public get company(): Nullable<CompanyBasic> {
    return this._company;
  }

  public get jobStartAt(): Nullable<Dayjs> {
    return this._job_start_at;
  }

  public get loadAt(): Nullable<Dayjs> {
    return this._load_at;
  }

  public get name() {
    return this._name;
  }

  public get notes(): string | null {
    return this._notes;
  }

  public get jobNotes(): string | null {
    return this._job_notes;
  }

  public get internalNotes(): string | null {
    return this._internal_notes;
  }

  public get state(): OrderState {
    return this._state;
  }

  public get truckCount(): number {
    return this._truck_count;
  }

  public set truckCount(value: number) {
    this._truck_count = value;
  }

  public get truckDelay(): number {
    return this._truck_delay;
  }

  public get trucksPerDelay(): number {
    return this._trucks_per_delay;
  }

  public get loadsPerTruck() {
    return this._loads_per_truck;
  }

  public get loadCycleAvg(): number {
    return this._load_cycle_avg || 0;
  }

  public get unitOfMeasure(): Nullable<ItemNameAndId> {
    return this._unit_of_measure;
  }

  public get account(): Nullable<AccountBasic> {
    return this._account;
  }

  public get orderId(): string {
    return this._order_id;
  }

  public get project(): Nullable<ProjectBasic> {
    return this._project;
  }
  public get projectName(): Nullable<string> {
    return this._project?.name;
  }

  public get material(): Nullable<BasicMaterial> {
    return this._material;
  }

  public get equipmentType() {
    return this._equipment_type;
  }

  public get quantity() {
    return this._quantity;
  }

  public get service(): Nullable<Service> {
    return this._service;
  }
  public get waypoints(): Nullable<WayPoint[]> {
    return this._waypoints;
  }
  public set pickUpWayPoints(waypoints: Nullable<WayPoint[]>) {
    this._waypoints = cloneDeep(waypoints);
  }

  public get pickUpWayPoints(): Nullable<WayPoint[]> {
    return this._pickUpWayPoints;
  }
  public get dropOffWayPoints(): Nullable<WayPoint[]> {
    return this._dropOffWayPoints;
  }
  public set dropOffWayPoints(waypoints: Nullable<WayPoint[]>) {
    this._waypoints = cloneDeep(waypoints);
  }
  public get stagingWayPoints(): Nullable<WayPoint[]> {
    return this._stagingWayPoints;
  }
  public set stagingWayPoints(waypoints: Nullable<WayPoint[]>) {
    this._stagingWayPoints = cloneDeep(waypoints);
  }
  public get weightWayPoints(): Nullable<WayPoint> {
    return this._waypoints?.find((waypoint) => waypoint.isWeighPoint);
  }
  public get vendorRate(): Nullable<RateBasicWithValue> {
    return this._vendor_rate;
  }
  public get customerRate(): Nullable<RateBasicWithValue> {
    return this._customer_rate;
  }
  public get driverRate(): Nullable<RateBasicWithValue> {
    return this._driver_rate;
  }
  public get phase(): Nullable<Phase> {
    return this._phase;
  }
  public set phase(phase: Nullable<Phase>) {
    this._phase = phase;
  }
  public get phases(): Nullable<Phase[]> {
    return this._phases;
  }
  public set phases(phases: Nullable<Phase[]>) {
    this._phases = phases;
  }
  public get materialTotals() {
    return this._material_totals;
  }
  public set materialTotals(value: OrderMaterialTotal[]) {
    this._material_totals = value;
  }
  public get isSalesContact(): boolean {
    return Boolean(this._sales_contact?.id.length);
  }
  public get isSupervisorContact(): boolean {
    return Boolean(this._supervisor_contact?.id.length);
  }
  public get isForemanContact(): boolean {
    return Boolean(this._foreman_contact?.id.length);
  }
  public get isCollaboratorContact(): boolean {
    return Boolean(this._collaborator_contact?.id.length);
  }
  public get salesContact(): Nullable<ExtendedContactItem> {
    return this._sales_contact;
  }
  public set salesContact(value: Nullable<ExtendedContactItem>) {
    this._sales_contact = value;
  }
  public get foremanContact(): Nullable<ExtendedContactItem> {
    return this._foreman_contact;
  }
  public set foremanContact(value: Nullable<ExtendedContactItem>) {
    this._foreman_contact = value;
  }
  public get collaboratorContact(): Nullable<ExtendedContactItem> {
    return this._collaborator_contact;
  }
  public set collaboratorContact(value: Nullable<ExtendedContactItem>) {
    this._collaborator_contact = value;
  }
  public get supervisorContact(): Nullable<ExtendedContactItem> {
    return this._supervisor_contact;
  }
  public set supervisorContact(value: Nullable<ExtendedContactItem>) {
    this._supervisor_contact = value;
  }
  public get allContacts(): ExtendedContactItem[] {
    const allContacts = [];
    if (this._sales_contact) {
      allContacts.push(this._sales_contact);
    }
    if (this._foreman_contact) {
      allContacts.push(this._foreman_contact);
    }
    if (this._collaborator_contact) {
      allContacts.push(this._collaborator_contact);
    }
    if (this._supervisor_contact) {
      allContacts.push(this._supervisor_contact);
    }
    return allContacts;
  }
  public get department(): Nullable<BasicDepartment> {
    return this._department;
  }

  public get serviceClass(): Nullable<BasicServiceClass> {
    return this._service_class;
  }

  public get actionRequired(): boolean {
    return this._action_required;
  }

  public get foremen(): Array<User> {
    return this._foremen || [];
  }

  public get collaborators() {
    return this._collaborators;
  }

  public get salespeople() {
    return this._salespeople || [];
  }

  public get defaultSiteRateSchedule(): Nullable<SiteRateSchedule> {
    return this._default_site_rate_schedule;
  }

  public get poJobNumber(): Nullable<string> {
    return this._po_job_number;
  }

  public get zone(): Nullable<string> {
    return this._zone;
  }

  public get externalId(): Nullable<string> {
    return this._external_id;
  }

  public get haulerRateType(): Nullable<RateType> {
    return this._hauler_rate_type;
  }
  public get jobTime(): Nullable<number> {
    return this._job_time;
  }
  public get unitsPerHour(): Nullable<number> {
    return this._units_per_hour;
  }
  public get jobQuantity(): Nullable<number> {
    return this._job_quantity;
  }
  public get jobUnitOfMeasure(): Nullable<ItemNameAndId> {
    return this._job_unit_of_measure;
  }
  public get orderNotes(): Nullable<string> {
    return this.notes;
  }

  public get sendingAccounts(): Array<AccountNestedProto> {
    return this._sending_accounts;
  }

  public get dispatchNumber(): Nullable<DispatchNumberBasic> {
    return this._dispatch_number;
  }

  public get contractNumber() {
    return this._contract_number;
  }

  public get customerRateValue() {
    return this._customer_rate_value;
  }

  public get customerRateType() {
    return this._customer_rate_type;
  }

  public get vendorPayCommissionPercentage() {
    return this._vendor_pay_commission_percentage;
  }
  public get driverPayCommissionPercentage() {
    return this._driver_pay_commission_percentage;
  }

  public get vendorRateValue() {
    return this._vendor_rate_value;
  }

  public get vendorRateType() {
    return this._vendor_rate_type;
  }

  public get driverRateValue() {
    return this._driver_rate_value;
  }

  public get driverRateType() {
    return this._driver_rate_type;
  }

  public get loadsPickupTimeMinutesAvg() {
    return this._loads_pickup_time_minutes_avg;
  }

  public get loadsDropoffTimeMinutesAvg() {
    return this._loads_dropoff_time_minutes_avg;
  }

  public get requester(): Nullable<UserBasic> {
    return this._requester;
  }

  public get orderSummary(): Nullable<OrderSummaryBasic> {
    return this._order_summary;
  }

  public get equipmentTypeGrossCapacity() {
    return this._equipment_type_gross_capacity;
  }

  public get equipmentTypeGrossCapacityUnitOfMeasure() {
    return this._equipment_type_gross_capacity_unit_of_measure;
  }

  public get pickupOnsiteMinutes() {
    return this._pickup_onsite_minutes;
  }

  public get dropoffOnsiteMinutes() {
    return this._dropoff_onsite_minutes;
  }

  public get pickupToDropoffTimeMinutes() {
    return this._pickup_to_dropoff_time_minutes;
  }

  public get pickupToDropoffDistanceMeters() {
    return this._pickup_to_dropoff_distance_meters;
  }

  public get expectedTotalLoads() {
    return this._expected_total_loads;
  }

  public get cycleTimeMinutes() {
    return this._cycle_time_minutes;
  }

  public get recurringLoads() {
    return this._recurring_loads;
  }

  public get materialRate() {
    return this._material_rate;
  }

  public get materialRateSite() {
    return this._material_rate_site;
  }

  public get materialRateUnitOfMeasure() {
    return this._material_rate_unit_of_measure;
  }

  public get materialRateSellPrice() {
    return this._material_rate_sell_price;
  }

  public get materialRateBuyPrice() {
    return this._material_rate_buy_price;
  }

  public get rejectReason(): Nullable<string> {
    return this._reject_reason;
  }

  public get stateEvents() {
    return this._state_events;
  }

  public get materialRateItemized() {
    return this._material_rate_itemized;
  }

  public get geofenceDelaySeconds() {
    return this._geofence_delay_seconds;
  }

  public set geofenceDelaySeconds(value: number | null) {
    this._geofence_delay_seconds = value;
  }

  public get loadUnitOfMeasure() {
    return this._load_unit_of_measure;
  }
  public get chargeToAccount() {
    return this._charge_to_account;
  }

  public get chaseMissingTickets() {
    return this._chase_missing_tickets;
  }

  public get liveEta() {
    return this._live_eta;
  }

  public get applicableRateCardCount() {
    return this._applicable_rate_card_count;
  }

  public get movingSiteSyncEnabled() {
    return this._moving_site_sync_enabled;
  }

  public get defaultDigitalTicketView() {
    return this._default_digital_ticket_view;
  }

  public get jobsSplittable() {
    return this._jobs_splittable;
  }

  public get prevailingWage() {
    return this._prevailing_wage;
  }

  public get taxRate() {
    return this._tax_rate;
  }

  public get taxFreight() {
    return this._tax_freight;
  }

  public get taxMaterial() {
    return this._tax_material;
  }

  public get taxAddOns() {
    return this._tax_add_ons;
  }

  public get taxRateSchedule() {
    return this._tax_rate_schedule;
  }

  public get discardedAt() {
    return this._discarded_at;
  }

  public get source() {
    return this._source;
  }

  public get intakeMetadata() {
    return this._intake_metadata;
  }

  public get messagingEnabled() {
    return this._messaging_enabled;
  }

  constructor(
    private _id: string,
    private _editable: boolean,
    private _created_at: Nullable<Dayjs>,
    private _company: Nullable<CompanyBasic>,
    private _job_start_at: Nullable<Dayjs>,
    private _load_at: Nullable<Dayjs>,
    private _notes: string | null,
    private _job_notes: string | null,
    private _job_time: Nullable<number>,
    private _internal_notes: string | null,
    private _state: OrderState,
    private _truck_count: number,
    private _truck_delay: number,
    private _trucks_per_delay: number,
    private _loads_per_truck: Nullable<number>,
    private _load_cycle_avg: number,
    private _unit_of_measure: Nullable<ItemNameAndId>,
    private _account: Nullable<AccountBasic>,
    private _order_id: string,
    private _project: Nullable<ProjectBasic>,
    private _material: Nullable<BasicMaterial>,
    private _equipment_type: Nullable<EquipmentTypeBasic>,
    private _quantity: Nullable<number>,
    private _service: Nullable<Service>,
    private _waypoints: Nullable<WayPoint[]>,
    private _pickUpWayPoints: Nullable<WayPoint[]>,
    private _dropOffWayPoints: Nullable<WayPoint[]>,
    private _stagingWayPoints: Nullable<WayPoint[]>,
    private _vendor_rate: Nullable<RateBasicWithValue>,
    private _customer_rate: Nullable<RateBasicWithValue>,
    private _phase: Nullable<Phase>,
    private _phases: Nullable<Phase[]>,
    private _sales_contact: Nullable<ExtendedContactItem>,
    private _foreman_contact: Nullable<ExtendedContactItem>,
    private _collaborator_contact: Nullable<ExtendedContactItem>,
    private _supervisor_contact: Nullable<ExtendedContactItem>,
    private _department: Nullable<BasicDepartment>,
    private _service_class: Nullable<BasicServiceClass>,
    private _action_required: boolean,
    private _foremen: Array<User> | null,
    private _name: Nullable<string>,
    private _po_job_number: Nullable<string>,
    private _zone: Nullable<string>,
    private _external_id: Nullable<string>,
    private _hauler_rate_type: Nullable<RateType>,
    private _units_per_hour: Nullable<number>,
    private _job_quantity: Nullable<number>,
    private _job_unit_of_measure: Nullable<ItemNameAndId>,
    private _sending_accounts: Array<AccountNestedProto>,
    private _dispatch_number: Nullable<DispatchNumberBasic>,
    private _contract_number: Nullable<string>,
    private _customer_rate_value: Nullable<number>,
    private _customer_rate_type: Nullable<RateType>,
    private _vendor_rate_value: Nullable<number>,
    private _vendor_rate_type: Nullable<RateType>,
    private _driver_rate: Nullable<RateBasicWithValue>,
    private _driver_rate_value: Nullable<number>,
    private _driver_rate_type: Nullable<RateType>,
    private _loads_pickup_time_minutes_avg: number,
    private _loads_dropoff_time_minutes_avg: number,
    private _requester: Nullable<UserBasic>,
    private _order_summary: Nullable<OrderSummaryBasic>,
    private _equipment_type_gross_capacity: number | null,
    private _equipment_type_gross_capacity_unit_of_measure: Nullable<ItemNameAndId>,
    private _pickup_onsite_minutes: number | null,
    private _dropoff_onsite_minutes: number | null,
    private _pickup_to_dropoff_time_minutes: number | null,
    private _pickup_to_dropoff_distance_meters: number | null,
    private _expected_total_loads: number | null,
    private _cycle_time_minutes: number | null,
    private _recurring_loads: boolean,
    private _collaborators: Array<UserBasic>,
    private _material_rate: MaterialRateTypeahead | null,
    private _material_rate_site: SiteBasic | null,
    private _material_rate_unit_of_measure: OrderUnitOfMeasure | null,
    private _material_rate_sell_price: number | null,
    private _material_rate_buy_price: number | null,
    private _reject_reason: Nullable<string>,
    private _state_events: OrderStateEvent[],
    private _material_rate_itemized: boolean,
    private _geofence_delay_seconds: number | null,
    private _load_unit_of_measure: Nullable<ItemNameAndId>,
    private _charge_to_account: Nullable<string>,
    private _chase_missing_tickets: boolean,
    private _moving_site_sync_enabled: boolean,
    private _default_digital_ticket_view: boolean,
    private _jobs_splittable: boolean,
    private _prevailing_wage: boolean,
    private _tax_rate: Nullable<number>,
    private _tax_freight: Nullable<boolean>,
    private _tax_material: Nullable<boolean>,
    private _tax_add_ons: Nullable<boolean>,
    private _tax_rate_schedule: Nullable<TaxRateScheduleBasic>,
    private _salespeople: Array<User>,
    private _default_site_rate_schedule: Nullable<SiteRateSchedule> = null,
    // Dispatch Live ETA opt-in; defaults from the parent Project server-side and cascades
    // down to this order's jobs (TRE-15771).
    private _live_eta: boolean = false,
    // How many rate cards apply to this order — its own when it has any, otherwise its
    // project's. Null when the payload withheld the count, which the backend does for
    // readers outside the order's owning company and for view-only users.
    private _applicable_rate_card_count: Nullable<number> = null,
    private _material_totals: OrderMaterialTotal[] = [],
    private _discarded_at: Dayjs | null = null,
    private _source: OrderSource = OrderSource.MANUAL,
    private _intake_metadata: IntakeMetadata | null = null,
    private _messaging_enabled: boolean = false,
    private _vendor_pay_commission_percentage: NonNullable<
      Order_Read['vendor_pay_commission_percentage']
    > | null = null,
    private _driver_pay_commission_percentage: NonNullable<
      Order_Read['driver_pay_commission_percentage']
    > | null = null,
  ) {}
}

export class OrderBasic {
  constructor(
    private _id: string,
    private _order_id: string,
    private _dispatch_number: DispatchNumberBasic | null,
    private _account: AccountBasic,
    private _job_time: number | null,
    private _equipment_type: EquipmentTypeBasic,
    private _name: string | null,
    private _po_job_number: string | null,
    private _contract_number: string | null,
    private _zone: string | null,
    private _notes: string | null,
    private _charge_to_account: string | null,
    private _state: OrderState,
    private _editable: boolean,
    private _default_digital_ticket_view: boolean,
    private _jobs_splittable: boolean,
    private _prevailing_wage: boolean,
    private _discarded_at: Dayjs | null = null,
  ) {}

  public get id() {
    return this._id;
  }

  public get orderId() {
    return this._order_id;
  }

  public get discardedAt() {
    return this._discarded_at;
  }

  public get dispatchNumber() {
    return this._dispatch_number;
  }

  public get account() {
    return this._account;
  }

  public get jobTime() {
    return this._job_time;
  }

  public get equipmentType() {
    return this._equipment_type;
  }

  public get name() {
    return this._name;
  }

  public get poJobNumber() {
    return this._po_job_number;
  }

  public get contractNumber() {
    return this._contract_number;
  }

  public get zone() {
    return this._zone;
  }

  public get notes() {
    return this._notes;
  }

  public get chargeToAccount() {
    return this._charge_to_account;
  }

  public get state() {
    return this._state;
  }

  public get editable() {
    return this._editable;
  }

  public get defaultDigitalTicketView() {
    return this._default_digital_ticket_view;
  }

  public get jobsSplittable() {
    return this._jobs_splittable;
  }

  public get prevailingWage() {
    return this._prevailing_wage;
  }

  public static parse(proto: Order_Read_Nested) {
    return new OrderBasic(
      proto.id,
      proto.order_id,
      proto.dispatch_number ? DispatchNumberBasic.parse(proto.dispatch_number) : null,
      AccountBasic.parse(proto.account),
      proto.job_time,
      EquipmentTypeBasic.parse(proto.equipment_type),
      proto.name,
      proto.po_job_number,
      proto.contract_number,
      proto.zone,
      proto.notes,
      proto.charge_to_account,
      proto.state,
      proto.editable,
      proto.default_digital_ticket_view,
      proto.jobs_splittable,
      proto.prevailing_wage,
      proto.discarded_at ? dayjs(proto.discarded_at) : null,
    );
  }
}

export class OrderIntakeDraft {
  constructor(
    private _load_at: Dayjs | null,
    private _truck_count: number | null,
    private _truck_delay: number | null,
    private _quantity: number | null,
    private _loads_per_truck: number | null,
    private _unit_of_measure: ItemNameAndId | null,
    private _notes: string | null,
    private _service_id: string | null,
  ) {}

  public get loadAt() {
    return this._load_at;
  }

  public get truckCount() {
    return this._truck_count;
  }

  public get truckDelay() {
    return this._truck_delay;
  }

  public get quantity() {
    return this._quantity;
  }

  public get loadsPerTruck() {
    return this._loads_per_truck;
  }

  public get unitOfMeasure() {
    return this._unit_of_measure;
  }

  public get notes() {
    return this._notes;
  }

  public get serviceId() {
    return this._service_id;
  }

  public static parse(proto: NonNullable<Order_Intake_Create['order']>) {
    return new OrderIntakeDraft(
      proto.load_at ? dayjs(proto.load_at) : null,
      proto.truck_count ?? null,
      proto.truck_delay ?? null,
      proto.quantity ?? null,
      proto.loads_per_truck ?? null,
      proto.unit_of_measure
        ? ItemNameAndId.parse({
            name: proto.unit_of_measure,
            id: proto.unit_of_measure,
          })
        : null,
      proto.notes ?? null,
      proto.service_id ?? null,
    );
  }
}

export class DeliveredQuantity {
  constructor(
    private _delivered: string,
    private _unit_of_measure: OrderUnitOfMeasure | null,
    private _material_name: string | null,
    private _total: string,
  ) {}

  public get delivered(): string {
    return this._delivered;
  }

  public get unitOfMeasure() {
    return this._unit_of_measure;
  }

  public get materialName() {
    return this._material_name;
  }

  public get total(): string {
    return this._total;
  }

  public static parse(proto: DeliveredQuantity_Read): DeliveredQuantity {
    return new DeliveredQuantity(
      proto.delivered,
      proto.unit_of_measure,
      proto.material_name,
      proto.total,
    );
  }
}

export class OrderSummaryBasic {
  constructor(
    private _accepted_jobs_count: number,
    private _assigned_jobs_count: number,
    private _delivered_loads_count: number,
    private _jobs_count: number,
    private _loads_count: number,
    private _sent_jobs_count: number,
    private _started_jobs_count: number,
    private _delivered_quantities: Array<DeliveredQuantity>,
    private _approved_loads_count: number,
    private _approved_payables: number,
    private _total_payables: number,
    private _approved_receivables: number,
    private _total_receivables: number,
    private _calculated_loads_count: number | null,
    private _material_gross_delivered_quantity: number | null,
    private _material_gross_unit_of_measure: ItemNameAndId | null,
    private _material_gross_requested_quantity: number | null,
    private _material_gross_ticketed_delivered_quantity: number | null,
    private _estimated_delivered_unit_of_measure: ItemNameAndId | null,
    private _estimated_delivered_quantity: number | null,
    private _file_attachments_count: number,
    private _total_tickets_count: number,
    private _total_tickets_quantity: number,
    private _first_ticket_at: Dayjs | null,
    private _first_load_unloaded_at: Dayjs | null,
    private _first_pickup_site_entry_at: Dayjs | null,
    private _last_pickup_site_exit_at: Dayjs | null,
    private _first_dropoff_site_entry_at: Dayjs | null,
    private _last_dropoff_site_exit_at: Dayjs | null,
    private _first_load_arrived_pickup_at: Dayjs | null,
    private _first_load_to_dropoff_at: Dayjs | null,
    private _travelled_distance_meters: string | null,
    private _idle_time_seconds: number,
  ) {}

  public get acceptedJobsCount(): number {
    return this._accepted_jobs_count;
  }

  public get assignedJobsCount(): number {
    return this._assigned_jobs_count;
  }

  public get deliveredLoadsCount(): number {
    return this._delivered_loads_count;
  }

  public get jobsCount(): number {
    return this._jobs_count;
  }

  public get loadsCount(): number {
    return this._loads_count;
  }

  public get sentJobsCount(): number {
    return this._sent_jobs_count;
  }

  public get startedJobsCount(): number {
    return this._started_jobs_count;
  }

  public get deliveredQuantities(): Array<DeliveredQuantity> {
    return this._delivered_quantities;
  }

  public get approvedLoadsCount() {
    return this._approved_loads_count;
  }

  public get approvedPayables() {
    return this._approved_payables;
  }

  public get totalPayables() {
    return this._total_payables;
  }

  public get approvedReceivables() {
    return this._approved_receivables;
  }

  public get totalReceivables() {
    return this._total_receivables;
  }

  public get calculatedLoadsCount() {
    return this._calculated_loads_count;
  }

  public get materialGrossDeliveredQuantity() {
    return this._material_gross_delivered_quantity;
  }

  public get materialGrossUnitOfMeasure() {
    return this._material_gross_unit_of_measure;
  }

  public get materialGrossRequestedQuantity() {
    return this._material_gross_requested_quantity;
  }
  public get materialGrossTicketedDeliveredQuantity() {
    return this._material_gross_ticketed_delivered_quantity;
  }
  public get estimatedDeliveredUnitOfMeasure() {
    return this._estimated_delivered_unit_of_measure;
  }

  public get estimatedDeliveredQuantity() {
    return this._estimated_delivered_quantity;
  }

  public get fileAttachmentsCount() {
    return this._file_attachments_count;
  }

  public get totalTicketsCount() {
    return this._total_tickets_count;
  }

  public get totalTicketsQuantity() {
    return this._total_tickets_quantity;
  }

  public get firstTicketAt() {
    return this._first_ticket_at;
  }

  public get firstLoadUnloadedAt() {
    return this._first_load_unloaded_at;
  }

  public get firstPickupSiteEntryAt() {
    return this._first_pickup_site_entry_at;
  }

  public get lastPickupSiteExitAt() {
    return this._last_pickup_site_exit_at;
  }

  public get firstDropoffSiteEntryAt() {
    return this._first_dropoff_site_entry_at;
  }

  public get lastDropoffSiteExitAt() {
    return this._last_dropoff_site_exit_at;
  }

  public get firstLoadArrivedPickupAt() {
    return this._first_load_arrived_pickup_at;
  }

  public get firstLoadToDropoffAt() {
    return this._first_load_to_dropoff_at;
  }

  public get travelledDistanceMeters() {
    return this._travelled_distance_meters;
  }

  public get idleTimeSeconds() {
    return this._idle_time_seconds;
  }

  public static parse(proto: OrderSummary_Read_Nested) {
    return new OrderSummaryBasic(
      proto.accepted_jobs_count,
      proto.assigned_jobs_count,
      proto.delivered_loads_count,
      proto.jobs_count,
      proto.loads_count,
      proto.sent_jobs_count,
      proto.started_jobs_count,
      proto.delivered_quantities.map(DeliveredQuantity.parse),
      proto.approved_loads_count,
      proto.approved_payables,
      proto.total_payables,
      proto.approved_receivables,
      proto.total_receivables,
      proto.calculated_loads_count,
      parseFiniteNumber(proto.material_gross_delivered_quantity),
      proto.material_gross_unit_of_measure
        ? ItemNameAndId.parse({
            name: proto.material_gross_unit_of_measure,
            id: proto.material_gross_unit_of_measure,
          })
        : null,
      parseFiniteNumber(proto.material_gross_requested_quantity),
      parseFiniteNumber(proto.material_gross_ticketed_delivered_quantity),
      proto.estimated_delivered_unit_of_measure
        ? ItemNameAndId.parse({
            name: proto.estimated_delivered_unit_of_measure,
            id: proto.estimated_delivered_unit_of_measure,
          })
        : null,
      parseFiniteNumber(proto.estimated_delivered_quantity),
      proto.file_attachments_count,
      proto.total_tickets_count,
      Number(proto.total_tickets_quantity),
      proto.first_ticket_at ? dayjs(proto.first_ticket_at) : null,
      proto.first_load_unloaded_at ? dayjs(proto.first_load_unloaded_at) : null,
      proto.first_pickup_site_entry_at ? dayjs(proto.first_pickup_site_entry_at) : null,
      proto.last_pickup_site_exit_at ? dayjs(proto.last_pickup_site_exit_at) : null,
      proto.first_dropoff_site_entry_at ? dayjs(proto.first_dropoff_site_entry_at) : null,
      proto.last_dropoff_site_exit_at ? dayjs(proto.last_dropoff_site_exit_at) : null,
      proto.first_load_arrived_pickup_at
        ? dayjs(proto.first_load_arrived_pickup_at)
        : null,
      proto.first_load_to_dropoff_at ? dayjs(proto.first_load_to_dropoff_at) : null,
      proto.travelled_distance_meters ?? null,
      proto.idle_time_seconds,
    );
  }
}

export class OrderTimeline {
  constructor(
    private _created_at: Dayjs,
    private _created_by: Principal_Read_Nested,
    private _object_changes: Record<string, string[]>,
  ) {}

  public get createdAt() {
    return this._created_at;
  }

  public get createdBy() {
    return this._created_by;
  }

  public get objectChanges() {
    return this._object_changes;
  }

  public static parse(proto: OrderTimeline_Read) {
    return new OrderTimeline(
      dayjs(proto.created_at),
      proto.created_by,
      proto.object_changes as Record<string, string[]>,
    );
  }
}

export class ImportedOrder {
  constructor(
    private _id: string,
    private _state: string,
    private _order_id: string | null,
  ) {}

  public static parse(proto: ImportedOrder_Read) {
    return new ImportedOrder(proto.id, proto.state, proto.order_id ?? null);
  }

  public get id() {
    return this._id;
  }

  public get state() {
    return this._state;
  }

  public get orderId() {
    return this._order_id;
  }
}
